import { Key, useState } from 'react'
import { Button, Flex, Segmented, Space, Table } from 'antd'
import { CloseOutlined, DownOutlined } from '@ant-design/icons'
import { BudgetRequest } from 'src/pages/budget/budgetRequests/types'
import { PAGE_SIZE } from 'src/pages/budget/budgetCategories/const'
import {
  getRequestListColumns,
  FilterTable,
} from 'src/pages/budget/budgetRequests/components'
import {
  filtersChangedBudgetListTeam,
  openModalRequestsReject,
  openModalRequestsApprove,
} from 'src/pages/budget/budgetRequests/model'
import { STATUS } from 'src/pages/budget/const'
import { showNotification } from 'src/features/notifications'
import { TStatusPeriod } from 'src/pages/budget/types'

type Props = {
  list: BudgetRequest[]
  filterList: BudgetRequest[]
  currentPeriodStatus?: TStatusPeriod
}
export const TeamTable = ({ list, filterList, currentPeriodStatus }: Props) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [viewMode, setViewMode] = useState<'pending' | 'all'>('pending')
  const displayedList =
    viewMode === 'pending'
      ? filterList.filter((request) => request.isAwaitingCurrentUser)
      : filterList

  const selectedRequests = list.filter(({ id }) => selectedRowKeys.includes(id))
  const hasSelected = selectedRequests.length > 0
  const notApprovedInReviewIds = selectedRequests
    .filter(({ status }) => {
      return status !== STATUS.SUBMITTED && status !== STATUS.IN_REVIEW
    })
    .map(({ id }) => id)

  const checkApprove = () => {
    if (notApprovedInReviewIds.length > 0) {
      const isSingle = notApprovedInReviewIds.length === 1
      showNotification({
        type: 'warning',
        message: 'Нельзя согласовать выбранные заявки',
        description: `${
          isSingle ? 'Заявка' : 'Заявки'
        } №${notApprovedInReviewIds.join(
          ', ',
        )} не в статусе «На согласовании» или «На проверке»`,
        duration: 15,
      })
      return
    }
    openModalRequestsApprove({
      items: selectedRequests.map((item) => ({
        id: item.id,
        description: item.description,
        availableApprovalSteps: item.availableApprovalSteps,
      })),
    })
  }

  const checkReject = () => {
    if (notApprovedInReviewIds.length > 0) {
      const isSingle = notApprovedInReviewIds.length === 1
      showNotification({
        type: 'warning',
        message: 'Нельзя отклонить выбранные заявки',
        description: `${
          isSingle ? 'Заявка' : 'Заявки'
        } №${notApprovedInReviewIds.join(
          ', ',
        )} не в статусе «На согласовании» или «На проверке»`,
        duration: 15,
      })
      return
    }
    openModalRequestsReject({
      items: selectedRequests.map((item) => ({
        id: item.id,
        description: item.description,
        availableApprovalSteps: item.availableApprovalSteps,
      })),
    })
  }

  return (
    <Flex vertical gap="middle" style={{ width: '100%' }}>
      <Flex justify="space-between" gap={8}>
        <Space wrap>
          <Segmented
            value={viewMode}
            options={[
              { label: 'Ждут меня', value: 'pending' },
              { label: 'Все заявки', value: 'all' },
            ]}
            onChange={(value) => {
              setSelectedRowKeys([])
              setViewMode(value as 'pending' | 'all')
            }}
          />
          {hasSelected && (
            <>
              <Button
                size="middle"
                color="green"
                variant="solid"
                icon={<DownOutlined />}
                iconPosition="start"
                key="add_approve"
                onClick={checkApprove}
              >
                Согласовать ({selectedRequests.length})
              </Button>
              <Button
                size="middle"
                variant="outlined"
                color="volcano"
                icon={<CloseOutlined />}
                iconPosition="start"
                key="add_reject"
                onClick={checkReject}
              >
                Отклонить ({selectedRequests.length})
              </Button>
            </>
          )}
        </Space>
        <FilterTable
          fields={[
            'articleName',
            'createdBy',
            'status',
            'products',
            'searchText',
          ]}
          onChange={(value) => {
            setSelectedRowKeys([])
            filtersChangedBudgetListTeam(value)
          }}
          list={list}
          initialValues={{ status: [STATUS.SUBMITTED] }}
        />
      </Flex>
      <Table
        dataSource={displayedList}
        tableLayout="fixed"
        rowSelection={{
          type: 'checkbox',
          columnWidth: 32,
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        pagination={
          displayedList.length > PAGE_SIZE
            ? {
                total: displayedList.length,
                defaultPageSize: PAGE_SIZE,
                showSizeChanger: true,
                position: ['bottomCenter'],
              }
            : false
        }
        columns={getRequestListColumns(
          [
            'id',
            'articleName',
            'itemName',
            'description',
            'amount',
            'createdBy',
            'productIds',
            'status',
            'actionForApprove',
          ],
          currentPeriodStatus,
          true,
        )}
        rowKey={(record) => record.id}
        rowClassName={(_, index) =>
          `ant_table_row_height ${index % 2 ? 'even-row' : 'odd-row'}`
        }
      />
    </Flex>
  )
}
